import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Star, Clock, Bike, MapPin, Plus, Minus, Leaf, Flame, ShoppingCart, Loader2, Package } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import Header from '../components/Header';
import { useApp } from '../context/AppContext';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import ShareButton from '../components/ShareButton';
import { catalogApi } from '../services/api';
import { toast } from 'sonner';

const VendorDetail = () => {
  const { id } = useParams();
  const nav = useNavigate();
  const { i18n } = useTranslation();
  const [vendor, setVendor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState(null);
  const { addToCart, cart, updateQty, cartCount, cartTotal, format } = useApp();

  useEffect(() => {
    let canceled = false;
    setLoading(true);
    catalogApi.vendor(id)
      .then((v) => {
        if (canceled) return;
        // Logistics vendors (courier / same-day / business-logistics) use the
        // dedicated booking flow instead of the menu storefront.
        const cat = String(v?.category || '').toLowerCase();
        if (['courier', 'package', 'logistics'].includes(cat)) {
          nav(`/book/${id}`, { replace: true });
          return;
        }
        setVendor(v);
        setActiveSection(v?.menu?.sections?.[0]?.title || null);
        // Auto-switch to vendor's preferred language (only if user hasn't picked one)
        const pickedByUser = localStorage.getItem('i18nextLng');
        if (v?.default_language && !pickedByUser) {
          i18n.changeLanguage(v.default_language);
        }
      })
      .catch(() => { if (!canceled) setVendor(null); })
      .finally(() => { if (!canceled) setLoading(false); });
    return () => { canceled = true; };
    // eslint-disable-next-line
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <div className="max-w-3xl mx-auto p-16 text-center"><Loader2 className="h-6 w-6 animate-spin text-emerald-600 mx-auto" /></div>
      </div>
    );
  }

  if (!vendor) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <div className="max-w-3xl mx-auto p-8 text-center text-slate-500">
          Store not found. <Link to="/" className="text-emerald-700 underline">Back to home</Link>
        </div>
      </div>
    );
  }

  const qtyOf = (itemId) => cart.items.find((i) => i.id === itemId)?.qty || 0;
  const handleAdd = (item) => {
    addToCart(vendor, item);
    toast.success(`Added · ${item.name}`);
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-32">
      <Header />

      <div className="relative h-56 sm:h-72 w-full overflow-hidden">
        <img src={vendor.cover} alt={vendor.name} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 via-slate-900/10 to-transparent" />
        <button onClick={() => nav(-1)} className="absolute top-4 left-4 h-9 w-9 rounded-full bg-white/95 backdrop-blur flex items-center justify-center hover:bg-white">
          <ArrowLeft className="h-4 w-4" />
        </button>
        <div className="absolute top-4 right-4">
          <ShareButton title={vendor.name} text={vendor.cuisine || ''} variant="pill" />
        </div>
      </div>

      <section className="max-w-5xl mx-auto px-4 sm:px-6 -mt-12 relative">
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5 sm:p-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                {(vendor.tags || []).map((t) => (
                  <Badge key={t} variant="secondary" className="bg-slate-100 text-slate-600 hover:bg-slate-100 font-normal">{t}</Badge>
                ))}
              </div>
              <h1 className="mt-2 text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900">{vendor.name}</h1>
              <div className="flex items-center flex-wrap gap-x-5 gap-y-1.5 mt-2 text-sm text-slate-600">
                <span className="inline-flex items-center gap-1"><Star className="h-4 w-4 fill-yellow-400 text-yellow-400" /> <b className="text-slate-900">{vendor.rating}</b> <span className="text-slate-400">({vendor.reviews.toLocaleString()} reviews)</span></span>
                <span className="inline-flex items-center gap-1"><Clock className="h-4 w-4 text-slate-400" /> {vendor.deliveryTime}</span>
                <span className="inline-flex items-center gap-1"><Bike className="h-4 w-4 text-slate-400" /> {format(vendor.deliveryFee, vendor.currency)} delivery</span>
                <span className="inline-flex items-center gap-1"><MapPin className="h-4 w-4 text-slate-400" /> {vendor.distance}</span>
              </div>
              <div className="mt-3">
                <button
                  type="button"
                  onClick={() => nav(`/vendor/${vendor.id}/gallery`)}
                  className="inline-flex items-center gap-1.5 text-xs bg-slate-900 text-white px-3 py-1.5 rounded-full hover:bg-slate-800"
                  data-testid="view-gallery-btn"
                >
                  <Package className="h-3.5 w-3.5" /> View gallery
                </button>
              </div>
            </div>
            {vendor.promo && (
              <Badge className="bg-emerald-600 hover:bg-emerald-600 text-white border-0 self-start hidden sm:flex">{vendor.promo}</Badge>
            )}
          </div>
        </div>
      </section>

      {vendor.default_shipping_size_id
        && vendor.category_default_shipping_size_id
        && vendor.default_shipping_size_id !== vendor.category_default_shipping_size_id
        && vendor.resolved_shipping_size
        && vendor.category_default_shipping_size && (
        <section className="max-w-5xl mx-auto px-4 sm:px-6 mt-4" data-testid="vendor-size-hint">
          <div className="rounded-xl border border-emerald-200 bg-emerald-50/70 px-4 py-3 flex items-start gap-3">
            <Package className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-emerald-900">
              This store ships in{' '}
              <b data-testid="vendor-size-hint-store">{vendor.resolved_shipping_size.name}</b>
              {vendor.resolved_shipping_size.price_multiplier < vendor.category_default_shipping_size.price_multiplier
                && ' — smaller and cheaper'}
              , instead of the usual{' '}
              <b data-testid="vendor-size-hint-category">{vendor.category_default_shipping_size.name}</b>
              {' '}for {(vendor.category || '').toLowerCase()}.
            </div>
          </div>
        </section>
      )}

      <section className="max-w-5xl mx-auto px-4 sm:px-6 mt-6">
        <div className="sticky top-16 z-30 bg-slate-50/95 backdrop-blur py-3 -mx-4 px-4 sm:mx-0 sm:px-0 border-b border-slate-200">
          <div className="flex gap-2 overflow-x-auto">
            {(vendor.menu?.sections || []).map((s) => (
              <button
                key={s.title}
                onClick={() => setActiveSection(s.title)}
                className={`px-3.5 h-9 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${activeSection === s.title ? 'bg-slate-900 text-white' : 'bg-white border border-slate-200 text-slate-700 hover:border-slate-300'}`}
              >
                {s.title}
              </button>
            ))}
          </div>
        </div>

        {(vendor.menu?.sections || []).map((s) => (
          <div key={s.title} className="mt-6">
            <h2 className="text-lg font-semibold text-slate-900">{s.title}</h2>
            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4">
              {s.items.map((item) => {
                const q = qtyOf(item.id);
                return (
                  <div key={item.id} className="bg-white rounded-xl border border-slate-200 p-4 flex gap-4 card-hover">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-slate-900 truncate">{item.name}</h3>
                        {item.popular && <Flame className="h-4 w-4 text-orange-500" />}
                        {item.veg && <Leaf className="h-4 w-4 text-emerald-600" />}
                      </div>
                      <p className="text-sm text-slate-500 mt-1 line-clamp-2">{item.description}</p>
                      <div className="mt-3 flex items-center justify-between">
                        <span className="font-semibold text-slate-900">{format(item.price, vendor.currency)}</span>
                        {q === 0 ? (
                          <Button onClick={() => handleAdd(item)} size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white h-8">
                            <Plus className="h-4 w-4 mr-1" /> Add
                          </Button>
                        ) : (
                          <div className="flex items-center gap-2">
                            <button onClick={() => updateQty(item.id, -1)} className="h-8 w-8 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center">
                              <Minus className="h-4 w-4" />
                            </button>
                            <span className="font-semibold w-6 text-center">{q}</span>
                            <button onClick={() => updateQty(item.id, +1)} className="h-8 w-8 rounded-full bg-emerald-600 text-white hover:bg-emerald-700 flex items-center justify-center">
                              <Plus className="h-4 w-4" />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="h-24 w-24 sm:h-28 sm:w-28 rounded-lg overflow-hidden bg-slate-100 shrink-0">
                      <img src={item.image} alt={item.name} className="h-full w-full object-cover" />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </section>

      {cartCount > 0 && (
        <div className="fixed bottom-4 inset-x-4 sm:inset-x-auto sm:right-6 sm:bottom-6 z-40">
          <button
            onClick={() => nav('/cart')}
            className="w-full sm:w-auto bg-slate-900 hover:bg-slate-800 text-white rounded-full shadow-2xl shadow-slate-900/30 px-5 py-3.5 flex items-center justify-between gap-4 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-full bg-emerald-600 flex items-center justify-center">
                <ShoppingCart className="h-4 w-4" />
              </div>
              <div className="text-left">
                <div className="text-xs text-slate-300">{cartCount} item{cartCount !== 1 ? 's' : ''}</div>
                <div className="text-sm font-semibold">View cart</div>
              </div>
            </div>
            <span className="font-semibold">{format(cartTotal, vendor.currency)}</span>
          </button>
        </div>
      )}
    </div>
  );
};

export default VendorDetail;
